import { Outlet, useNavigate, useParams } from 'react-router-dom'
import { invoicesVisibility } from 'modules/project_management/lib/utils'
import { Box, useEffect } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'
import {
  AvailableCurrencyFragment,
  ProjectSaleInvoicesPageDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FeatureAvailabilityType } from 'modules/core/hooks/useFeatureAvailability'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { SaleInvoicesListTable } from 'modules/finance/components/Invoices/SaleInvoicesListTable'
import { useArchiveSaleInvoiceDialog } from 'modules/finance/hooks/sale_invoices/useArchiveSaleInvoiceDialog'
import EmptyState from 'modules/project_management/components/EmptyState'
import { UpsellProjectInvoices } from 'modules/project_management/components/upsells/UpsellProjectInvoices'

import { ProjectDetailsPageSaleInvoicesPageInsights } from './Insights'

interface ProjectSalesInvoicesTableProps {
  projectId: string
  projectCurrency: string
  companyId: string
  locale: string
  showDeleteModalForInvoiceId: (invoiceId: string) => void
  availableCurrencies: AvailableCurrencyFragment[]
  createInvoicesFeatureAvailable: boolean
  usage: number
  usageLimit: number
  currentEmployeeId: string
}

const ProjectSalesInvoicesTable = ({
  projectId,
  projectCurrency,
  companyId,
  locale,
  showDeleteModalForInvoiceId,
  availableCurrencies,
  createInvoicesFeatureAvailable,
  usage,
  usageLimit,
  currentEmployeeId,
}: ProjectSalesInvoicesTableProps) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  return (
    <>
      <Box paddingBottom="s24">
        <ProjectDetailsPageSaleInvoicesPageInsights
          projectId={projectId}
          projectCurrency={projectCurrency}
        />
      </Box>
      <SaleInvoicesListTable
        companyId={companyId}
        locale={locale}
        legalEntitiesOptions={[]}
        availableCurrencies={availableCurrencies}
        showDeleteModalForInvoiceId={showDeleteModalForInvoiceId}
        projectId={projectId}
        onClickAdd={() => {
          navigate(
            navigation.projects.id.details.sale_invoices.invoices.create.fullPath({
              id: projectId,
            })
          )
        }}
        detailPath={(invoiceId) =>
          navigation.projects.id.details.sale_invoices.invoices.invoiceId.fullPath({
            id: projectId,
            invoiceId,
          })
        }
        onClickExport={() => {
          track(trackingEvents.accounting.saleInvoices.clickExport)
          navigate(
            navigation.projects.id.details.sale_invoices.invoices.export.fullPath({
              id: projectId,
            })
          )
        }}
        createInvoicesFeatureAvailable={createInvoicesFeatureAvailable}
        usage={usage}
        usageLimit={usageLimit}
        projectsOrigin
        currentEmployeeId={currentEmployeeId}
        pageDetailPath={(invoiceId) =>
          navigation.projects.id.details.sale_invoices.invoices.detail.fullPath({
            id: projectId,
            invoiceId,
          })
        }
      />
    </>
  )
}

const ProjectSaleInvoicesPage = () => {
  const { access, company, employee: currentEmployee, canRequestUpsell } = useCurrentSession()
  const locale = access.locale
  const { showArchiveSaleInvoiceDialog } = useArchiveSaleInvoiceDialog()
  const { id } = useParams()
  const projectId = id ?? ''
  const isSaleInvoiceDetailPage = location.pathname.includes('/sale_invoices/invoices/detail/')

  useEffect(() => {
    track(trackingEvents.projectManagement.salesInvoices.viewInvoicesTab)
  }, [])

  const { data } = useQuery(ProjectSaleInvoicesPageDocument, {
    variables: {
      projectId,
      companyId: company.id,
      featureIds: [
        Features.sales_invoices_creation,
        Features.projects_costs,
        Features.sales_invoices_management,
      ],
    },
  })

  const createInvoicesFeatureAvailable =
    data?.finance?._policies?.financialDocumentsCreateSale?.can ?? false

  const availableCurrencies = data
    ? data.apiCore.currenciesConnection.edges.map((edge) => edge.node)
    : []

  const featureUsage: FeatureAvailabilityType = data?.companies.featureAvailabilitiesConnection
    .nodes[0] ?? {
    available: false,
    id: Features.sales_invoices_creation,
    _internalType: 'feature',
  }

  const usage = featureUsage?.usage || 0
  const usageLimit = featureUsage?.usageLimit || -1

  const hasProjectsCostsFeature = !!data?.companies.featureAvailabilitiesConnection.nodes.find(
    (feature) => feature.id === Features.projects_costs
  )?.available

  const hasSalesInvoicesManagementFeature =
    !!data?.companies.featureAvailabilitiesConnection.nodes.find(
      (feature) => feature.id === Features.sales_invoices_management
    )?.available

  const project = data?.projectManagement.project
  const projectCurrency = project?.legalEntity?.currency ?? 'EUR'

  const canManageProjectSalesInvoices = project?._policies?.manageProjectSalesDocuments.can ?? false
  // manageProjectSalesDocuments policy checks if the user is admin or owner && permission to manage invoices.
  // assignOwner policy checks if the user is an admin or an owner of the project. We should rename it to isAdminOrOwner.
  const isOwnerOrAdmin = project?._policies?.assignOwner.can ?? false
  const isSpanishCompany =
    company.legalEntities.find((legalEntity) => legalEntity.main)?.country === 'es'

  const { canSeeSalesInvoicesPageContent, canSeeSalesInvoicesUpsell } =
    invoicesVisibility(
      hasProjectsCostsFeature,
      canRequestUpsell,
      isOwnerOrAdmin,
      isSpanishCompany,
      hasSalesInvoicesManagementFeature
    )

  return (
    <>
      {canSeeSalesInvoicesUpsell && (
        <UpsellProjectInvoices projectsMissing={!hasProjectsCostsFeature} />
      )}

      {canSeeSalesInvoicesPageContent &&
        (canManageProjectSalesInvoices ? (
          <>
            {!isSaleInvoiceDetailPage && (
              <ProjectSalesInvoicesTable
                projectId={projectId}
                projectCurrency={projectCurrency}
                companyId={company.id}
                locale={locale}
                availableCurrencies={availableCurrencies}
                createInvoicesFeatureAvailable={createInvoicesFeatureAvailable}
                usage={usage}
                usageLimit={usageLimit}
                currentEmployeeId={currentEmployee?.id || '-1'}
                showDeleteModalForInvoiceId={(invoiceId) =>
                  showArchiveSaleInvoiceDialog({ invoiceId })
                }
              />
            )}
            <Outlet
              context={{
                project,
              }}
            />
          </>
        ) : (
          <EmptyState
            title={i18n.t('project_management.sale_invoices.empty_state_no_permissions.title')}
            description={i18n.t(
              'project_management.sale_invoices.empty_state_no_permissions.description'
            )}
            emoji={'✏️'}
          />
        ))}
    </>
  )
}

export default ProjectSaleInvoicesPage
